import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Tooltip,
  Fab,
  InputAdornment,
  Card,
  CardContent,
  DialogContentText,
  CircularProgress,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { 
  getSchools, 
  getSchoolById,
  createSchool,
  updateSchool,
  deleteSchool 
} from '../../services/api';

const SchoolsManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({
    totalSchools: 0,
    activeSchools: 0,
    inactiveSchools: 0
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [formMode, setFormMode] = useState('add');

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await getSchools();
      if (response.success) {
        setSchools(response.data || []);
        calculateStats(response.data || []);
      } else {
        showError(response.message || 'Failed to load schools');
      }
    } catch (err) {
      showError(err.message || 'Failed to load schools');
      console.error('Schools error:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (schoolsData) => {
    const activeSchools = schoolsData.filter(school => school.status === 'active').length;
    const inactiveSchools = schoolsData.filter(school => school.status === 'inactive').length;
    
    setStats({
      totalSchools: schoolsData.length,
      activeSchools,
      inactiveSchools
    });
  };

  const showSuccess = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const showError = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleDeleteClick = (school) => {
    setSelectedSchool(school);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteSchool(selectedSchool.id);
      if (response.success) {
        showSuccess('School deleted successfully!');
        setOpenDeleteDialog(false);
        fetchSchools();
      } else {
        showError(response.message || 'Failed to delete school');
      }
    } catch (err) {
      showError(err.message || 'Failed to delete school');
    }
  };

  const handleView = async (school) => {
    try {
      const response = await getSchoolById(school.id);
      if (response.success) {
        setSelectedSchool(response.data);
        setOpenViewDialog(true);
      } else {
        showError(response.message || 'Failed to load school details');
      }
    } catch (err) {
      showError(err.message || 'Failed to load school details');
    }
  };

  const handleEdit = (school) => {
    setSelectedSchool(school);
    setFormMode('edit');
    setOpenFormDialog(true);
  };

  const handleAddSchool = () => {
    setSelectedSchool(null);
    setFormMode('add');
    setOpenFormDialog(true);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'error';
  };

  const getStatusText = (status) => {
    return status === 'active' ? 'Active' : 'Inactive';
  };

  const filteredSchools = schools.filter(school => {
    // Apply search filter
    if (searchQuery && 
        !school.school_name?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !school.school_code?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !school.contact_person?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const SchoolForm = () => {
    const [formData, setFormData] = useState({
      school_name: '',
      school_code: '',
      school_address: '',
      school_phone: '',
      school_email: '',
      contact_person: '',
      contact_person_phone: '',
      contact_person_email: '',
      status: 'active',
      notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    useEffect(() => {
      if (formMode === 'edit' && selectedSchool) {
        setFormData({
          school_name: selectedSchool.school_name || '',
          school_code: selectedSchool.school_code || '',
          school_address: selectedSchool.school_address || '',
          school_phone: selectedSchool.school_phone || '',
          school_email: selectedSchool.school_email || '',
          contact_person: selectedSchool.contact_person || '',
          contact_person_phone: selectedSchool.contact_person_phone || '',
          contact_person_email: selectedSchool.contact_person_email || '',
          status: selectedSchool.status || 'active',
          notes: selectedSchool.notes || ''
        });
      } else {
        generateSchoolCode();
      }
    }, [formMode, selectedSchool]);

    const generateSchoolCode = () => {
      const prefix = 'SCH';
      const year = new Date().getFullYear().toString().slice(-2);
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      setFormData(prev => ({ ...prev, school_code: `${prefix}${year}${random}` }));
    };

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!formData.school_name) {
        setFormError('School name is required');
        return;
      }

      try {
        setSaving(true);
        
        if (formMode === 'edit' && selectedSchool) {
          const response = await updateSchool(selectedSchool.id, formData);
          if (response.success) {
            showSuccess('School updated successfully!');
            setOpenFormDialog(false);
            fetchSchools();
          } else {
            setFormError(response.message || 'Failed to update school');
          }
        } else {
          const response = await createSchool(formData);
          if (response.success) {
            showSuccess('School added successfully!');
            setOpenFormDialog(false);
            fetchSchools();
          } else {
            setFormError(response.message || 'Failed to add school');
          }
        }
      } catch (err) {
        setFormError(err.message || 'Failed to save school');
      } finally {
        setSaving(false);
      }
    };

    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3 }}>
          {formMode === 'edit' ? 'Edit School' : 'Add New School'}
        </Typography>
        
        {formError && (
          <Alert severity="error" sx={{ mb: 3 }}>{formError}</Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Name *"
                name="school_name"
                value={formData.school_name}
                onChange={handleChange}
                required
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Code"
                name="school_code"
                value={formData.school_code}
                onChange={handleChange}
                margin="normal"
                InputProps={{ readOnly: formMode === 'edit' }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="School Address"
                name="school_address"
                value={formData.school_address}
                onChange={handleChange}
                multiline
                rows={2}
                margin="normal"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Phone"
                name="school_phone"
                value={formData.school_phone}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="School Email"
                name="school_email"
                value={formData.school_email}
                onChange={handleChange}
                type="email"
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person Phone"
                name="contact_person_phone"
                value={formData.contact_person_phone}
                onChange={handleChange}
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Person Email"
                name="contact_person_email"
                value={formData.contact_person_email}
                onChange={handleChange}
                type="email"
                margin="normal"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={3}
                margin="normal"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenFormDialog(false)}
              disabled={saving}
              startIcon={<CloseIcon />}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              type="submit"
              disabled={saving}
              sx={{ backgroundColor: '#3498db' }}
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {saving ? 'Saving...' : (formMode === 'edit' ? 'Update School' : 'Save School')}
            </Button>
          </Box>
        </form>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#2c3e50', fontWeight: 'bold' }}>
            Schools Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredSchools.length} schools found • {stats.activeSchools} active • {stats.inactiveSchools} inactive
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSchools}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#3498db',
              '&:hover': { backgroundColor: '#2980b9' }
            }}
            onClick={handleAddSchool}
          >
            Add School
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Schools
                  </Typography>
                  <Typography variant="h4">{stats.totalSchools}</Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#3498db', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Active Schools
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.activeSchools}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#2ecc71', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Inactive Schools
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.inactiveSchools}
                  </Typography>
                </Box>
                <SchoolIcon sx={{ fontSize: 40, color: '#e74c3c', opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search schools..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Schools Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>School Code</strong></TableCell>
              <TableCell><strong>School Name</strong></TableCell>
              <TableCell><strong>Contact Person</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSchools.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No schools found. Try adjusting your search.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={handleAddSchool}
                    sx={{ mt: 2 }}
                  >
                    Add New School
                  </Button>
                </TableCell>
              </TableRow>
            ) : (
              filteredSchools.map((school) => (
                <TableRow 
                  key={school.id}
                  hover
                  sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {school.school_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{school.school_name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <LocationIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {school.school_address || 'No address'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{school.contact_person || 'Not specified'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PhoneIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {school.school_phone || 'Not specified'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {school.school_email || 'Not specified'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(school.status)} 
                      size="small"
                      color={getStatusColor(school.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleView(school)}
                          sx={{ color: '#3498db' }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit School">
                        <IconButton 
                          size="small"
                          sx={{ color: '#f39c12' }}
                          onClick={() => handleEdit(school)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete School">
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(school)}
                          sx={{ color: '#e74c3c' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        {selectedSchool && (
          <>
            <DialogTitle>
              <Typography variant="h6">
                School Details: {selectedSchool.school_name}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">School Code:</Typography>
                  <Typography variant="body1">{selectedSchool.school_code}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={getStatusText(selectedSchool.status)} 
                    size="small"
                    color={getStatusColor(selectedSchool.status)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Address:</Typography>
                  <Typography variant="body1">{selectedSchool.school_address || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Phone:</Typography>
                  <Typography variant="body1">{selectedSchool.school_phone || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Email:</Typography>
                  <Typography variant="body1">{selectedSchool.school_email || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contact Person:</Typography>
                  <Typography variant="body1">{selectedSchool.contact_person || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contact Person Phone:</Typography>
                  <Typography variant="body1">{selectedSchool.contact_person_phone || 'Not specified'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Contact Person Email:</Typography>
                  <Typography variant="body1">{selectedSchool.contact_person_email || 'Not specified'}</Typography>
                </Grid>
                {selectedSchool.statistics && (
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Statistics</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Total Projects:</Typography>
                        <Typography variant="body1">{selectedSchool.statistics.total_projects || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Active Projects:</Typography>
                        <Typography variant="body1">{selectedSchool.statistics.active_projects || 0}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Assignments:</Typography>
                        <Typography variant="body1">{selectedSchool.statistics.assignments_count || 0}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
                {selectedSchool.notes && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Notes:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedSchool.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
              <Button 
                onClick={() => {
                  setOpenViewDialog(false);
                  handleEdit(selectedSchool);
                }}
                variant="contained"
                sx={{ backgroundColor: '#f39c12' }}
              >
                Edit School
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={openFormDialog} onClose={() => setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <SchoolForm />
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete School</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedSchool?.school_name}"? 
            <Typography color="error" sx={{ mt: 1 }}>
              Warning: This action cannot be undone. If this school has projects, you cannot delete it.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Quick Add FAB */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          backgroundColor: '#3498db',
        }}
        onClick={handleAddSchool}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default SchoolsManagement;